import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Rocket, Timer, Check, Sparkles } from "lucide-react";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

export function useCountdown(endsAt) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) return undefined;
    const tick = () => {
      const ms = new Date(endsAt) - Date.now();
      const s = Math.max(0, Math.floor(ms / 1000));
      setLeft({ d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60, over: ms <= 0 });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
}

function Countdown({ endsAt, light }) {
  const { t } = useTranslation();
  const left = useCountdown(endsAt);
  if (!left) return null;
  const cell = (v, l) => (
    <div className={`flex min-w-[3.2rem] flex-col items-center rounded-md px-2 py-1 ${light ? "bg-white/15" : "bg-black/5"}`}>
      <span className="font-display text-2xl font-extrabold tabular-nums leading-none">{pad(v)}</span>
      <span className="text-[10px] uppercase tracking-wider opacity-75">{l}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5" data-testid="launch-countdown">
      <Timer className="mr-1 h-4 w-4 opacity-80" />
      {cell(left.d, t("days"))}{cell(left.h, t("hrs"))}{cell(left.m, t("min"))}{cell(left.s, t("sec"))}
    </div>
  );
}

const fmt = (n) => `$${Number(n).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;

export function LaunchOfferCard({ publicMode = false }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const { data } = useQuery({
    queryKey: ["launch-offer", publicMode],
    queryFn: async () => (await api.get(publicMode ? "/billing/launch-offer/public" : "/billing/launch-offer")).data,
  });
  if (!data || !data.open) return null;
  if (!publicMode && data.eligible === false) return null;

  const buy = async (plan) => {
    setBusy(plan);
    try {
      const { data: r } = await api.post("/payments/launch-checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <section className="relative overflow-hidden rounded-2xl bg-[#0f172a] p-6 text-white shadow-xl sm:p-8" data-testid="launch-offer-card">
      <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-black">
            <Rocket className="h-3.5 w-3.5" /> {t("Product Launch Offer")}
          </div>
          <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
            {t("2 years of KlenaPro from just $10 a month.")}
          </h2>
          <p className="mt-2 text-sm text-white/80">
            {t("Pay once, up front, and lock in {{months}} months of full access for your whole team. Offer ends 31 December 2026 — after your 2 years, billing simply returns to the standard per-cleaner, per-room weekly rate.", { months: data.term_months })}
          </p>
          <div className="mt-4"><Countdown endsAt={data.ends_at} light /></div>
          <div className="mt-4 space-y-1 rounded-lg border border-white/15 bg-white/5 p-3 text-xs text-white/80" data-testid="launch-savings-example">
            {data.plans.map((p) => (
              <div key={p.key}>
                <Sparkles className="mr-1 inline h-3.5 w-3.5 text-amber-300" />
                {t("{{plan}} example: {{c}} cleaners + {{r}} rooms on standard pricing = {{weekly}}/week ≈ {{total}} over 2 years.", {
                  plan: p.name, c: p.example.cleaners, r: p.example.rooms, weekly: `$${p.example.weekly}`, total: fmt(p.example.total),
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-[30rem]">
          {data.plans.map((p, i) => (
            <div key={p.key} className={`relative flex flex-col rounded-xl p-4 ${i === 0 ? "bg-white text-slate-900" : "bg-amber-400 text-black"}`} data-testid={`launch-plan-${p.key}`}>
              {i === 0 && <span className="absolute -top-2 right-3 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-extrabold uppercase text-white">{t("Most popular")}</span>}
              <div className="text-xs font-bold uppercase tracking-wider opacity-70">{p.name}</div>
              <div className="mt-1 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold">${p.monthly}</span>
                <span className="mb-1 text-sm opacity-70">/{t("month")}</span>
              </div>
              <div className="text-xs opacity-80">{t("{{total}} NZD paid once for {{months}} months", { total: fmt(p.total), months: data.term_months })}</div>
              <ul className="mt-3 space-y-1 text-sm">
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4" /> {t("Up to {{n}} cleaners", { n: p.cleaners })}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4" /> {t("Up to {{n}} rooms", { n: p.rooms })}</li>
                <li className="flex items-center gap-1.5"><Check className="h-4 w-4" /> {t("Every feature, every update")}</li>
              </ul>
              <div className="mt-3 rounded-md bg-black/5 px-2 py-1.5 text-xs font-semibold" data-testid={`launch-savings-${p.key}`}>
                {t("Save {{amount}} ({{pct}}%) vs standard pricing for {{c}} cleaners + {{r}} rooms", { amount: fmt(p.savings_example), pct: p.savings_pct, c: p.example.cleaners, r: p.example.rooms })}
              </div>
              {publicMode ? (
                <Button asChild className="mt-3 w-full font-bold" data-testid={`launch-signup-${p.key}`}>
                  <Link to="/signup">{t("Start free trial, then claim")}</Link>
                </Button>
              ) : (
                <Button onClick={() => buy(p.key)} disabled={!!busy} className="mt-3 w-full font-bold" data-testid={`launch-buy-${p.key}`}>
                  {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Claim this offer")}
                </Button>
              )}
            </div>
          ))}
          <p className="text-[11px] text-white/60 sm:col-span-2">
            {t("Savings compare each plan's example team at the current standard rates (${{rc}}/cleaner and ${{rr}}/room per week; ${{vol}} beyond {{c}} cleaners / {{r}} rooms). Extra cleaners or rooms above the plan limit are billed at the standard per-unit rate.", { rc: data.rate_cleaner, rr: data.rate_room, vol: data.volume_rate, c: data.tier1_cleaners, r: data.tier1_rooms })}
          </p>
        </div>
      </div>
    </section>
  );
}

export function LaunchOfferStrip() {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ["launch-offer", false], queryFn: async () => (await api.get("/billing/launch-offer")).data });
  const left = useCountdown(data?.ends_at);
  if (!data?.open || !data.eligible || !left) return null;
  return (
    <Link to="/manager/billing" className="flex items-center justify-center gap-2 bg-amber-400 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-300" data-testid="launch-offer-strip">
      <Rocket className="h-3.5 w-3.5" />
      {t("Launch Offer: 2 years from $10/month — ends in {{d}} days", { d: left.d })}
      <span className="underline">{t("See offer")}</span>
    </Link>
  );
}

export function LaunchPlanStatus({ plan, overflow }) {
  const { t } = useTranslation();
  if (!plan) return null;
  const ends = new Date(plan.ends_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm" data-testid="launch-plan-status">
      <div className="flex items-center gap-2 font-bold text-emerald-900"><Rocket className="h-4 w-4" /> {t("Launch Offer plan active — {{name}}", { name: plan.name })}</div>
      <p className="mt-1 text-emerald-900/80">
        {t("Prepaid until {{date}}. Includes up to {{c}} cleaners and {{r}} rooms. After that date billing returns to the standard per-cleaner, per-room weekly rate.", { date: ends, c: plan.cleaners, r: plan.rooms })}
      </p>
      {overflow && (overflow.cleaners > 0 || overflow.rooms > 0) && (
        <p className="mt-1 font-semibold text-amber-800" data-testid="launch-overflow">
          {t("Above plan limit: {{c}} cleaner(s), {{r}} room(s) — billed at the standard per-unit rate.", { c: overflow.cleaners, r: overflow.rooms })}
        </p>
      )}
    </div>
  );
}
